import { parseNoteName } from "./notes";
import { createEmptyClip, type ClipIR, type PatternTrack, type RowMarker } from "./types";

/**
 * Maurice Ravel — Boléro, M. 81 (1928, public domain): the complete 340-bar
 * form rendered for eight monophonic Curva voices.
 *
 * ## Form
 * One unbroken movement in 3/4, C major. An unnumbered 20-bar opening section
 * plus Ravel's 17 numbered sections of 18 bars, plus a 14-bar closing section:
 * `20 + 17×18 + 14 = 340`. Two 16-bar tunes alternate over an unchanging
 * two-bar snare ostinato, entering two bars into each section, so statement `k`
 * enters at bar `5 + 18k`. The eighteen statements run
 * `A A B B · A A B B · A A B B · A A B B · A B`, and the final `B` breaks into
 * an eight-bar swerve to E major (bar 327) before crashing back to C for the
 * F-minor/C-major cadence of the last two bars.
 *
 * ## Grid
 * `stepDivision = 24` — 24 subdivisions per whole note, i.e. six per quarter and
 * eighteen per 3/4 bar. That is the coarsest grid that places the ostinato's
 * sixteenth-note triplets exactly, which matters because the ostinato is the
 * piece's engine and never stops. Plain sixteenths (1.5 steps) therefore land on
 * the nearest sextuplet slot, so runs come out with a shuffle rather than dead
 * even; the trade is deliberate, since the alternative grid limps the triplets
 * instead. Positions are carried internally in twelfths of a quarter (`Ev`
 * durations) so both subdivisions stay exact until the final rounding, and `cut`
 * gates are computed from the true duration, keeping note lengths honest even
 * where the onset was nudged.
 *
 * ## Tracks
 * Ravel recolours one tune eighteen times; with eight mono voices the trick is
 * to move it between four melodic tracks and change which of them double it, at
 * which transposition. `Traverso`/`Hautbois`/`Clarion`/`Music Box` share the
 * melody (Music Box uses Ring Spire); the two `Pizz` tracks share Baroque Fiddle
 * so the string chords ring as one timbre; `Contrabass` walks the tonic/dominant
 * pizzicato; `Tambour` is a pitched Cavern Tom one-shot (no snare sample). Idle
 * melodic tracks join the pitched ostinato, exactly as Ravel's spare flutes and
 * muted trumpets do.
 *
 * ## Crescendo
 * `volume` is the only dynamic control, so a single piecewise-linear ramp
 * through Ravel's marked dynamics (pp, p at Fig. 2, mp at 8, mf at 9, f at 11,
 * ff at 16) is evaluated per bar and scaled per role. Voice count grows with it:
 * three tracks sound at bar 1, all eight by the coda.
 */

const BPM = 72; // "Tempo di Bolero moderato assai", quarter = 72
const STEP_DIVISION = 24;
const STEPS_PER_BAR = 18;
const BAR_COUNT = 340;
export const BOLERO_TOTAL_STEPS = BAR_COUNT * STEPS_PER_BAR;

/** Twelfths of a quarter: a sixteenth is 3, a sixteenth-triplet 2, an eighth 6. */
const TICKS_PER_QUARTER = 12;
const TICKS_PER_BAR = 3 * TICKS_PER_QUARTER;
const TICKS_PER_STEP = TICKS_PER_QUARTER / 6;

const TRAVERSO = 0;
const HAUTBOIS = 1;
const CLARION = 2;
const MUSIC_BOX = 3;
const PIZZ_HI = 4;
const PIZZ_LO = 5;
const CONTRABASS = 6;
const TAMBOUR = 7;

export const BOLERO_TRACK_NAMES = [
  "Traverso",
  "Hautbois",
  "Clarion",
  "Music Box",
  "Pizz Hi",
  "Pizz Lo",
  "Contrabass",
  "Tambour",
] as const;

export const BOLERO_TRACK_PRESETS = [
  "Silver Traverso",
  "Nasal Hautbois",
  "Gilded Clarion",
  "Ring Spire",
  "Baroque Fiddle",
  "Baroque Fiddle",
  "Amber Cello",
  "Cavern Tom — tambour",
] as const;

/** Per-track trim; the music box sounds one to two octaves up and needs holding back. */
const TRACK_TRIM = [1, 1, 0.92, 0.58, 1, 1, 1, 1] as const;

const LEAD_GAIN = 0.95;
const DOUBLE_GAIN = 0.68;
const RYTHMIQUE_GAIN = 0.42;
const PIZZ_GAIN = 0.5;
const BASS_GAIN = 0.6;
const TAMBOUR_GAIN = 0.44;

/** Gate lengths in steps for the short, articulated parts. */
const GATE = {
  tambour: 0.3,
  pizz: 1.5,
  bassPizz: 2.5,
  bassArco: 1.4,
  arpeggio: 0.9,
  hammer: 0.8,
} as const;

/** Ravel's marked dynamics as `[bar, level]`, read as one unbroken ramp. */
const DYNAMIC_ANCHORS: readonly (readonly [number, number])[] = [
  [0, 0.16], // pp
  [38, 0.22], // p at Fig. 2
  [146, 0.36], // mp at Fig. 8
  [164, 0.46], // mf at Fig. 9
  [200, 0.58], // f at Fig. 11
  [290, 0.78], // ff at Fig. 16
  [326, 0.92],
  [BAR_COUNT - 1, 1],
];

/** The kernel has no snare sample, so the tambour is a dry pitched click. */
const TAMBOUR_NOTE = "D3";

/** Pitch of the ostinato when a wind doubles it (Ravel's `sol'` pizzicato/rythmique). */
const RYTHMIQUE_NOTE = "G4";

/**
 * The two-bar snare ostinato as tick offsets inside each bar: an eighth plus a
 * sixteenth-triplet twice over, then two eighths; the answering bar dissolves
 * its third beat into nine triplets.
 */
const OSTINATO: readonly (readonly number[])[] = [
  [0, 6, 8, 10, 12, 18, 20, 22, 24, 30],
  [0, 6, 8, 10, 12, 18, 20, 22, 24, 26, 28, 30, 32, 34],
];

/** `[note, durationTicks]`; `null` is a rest and tied notes are merged into one. */
type Ev = readonly [string | null, number];

/** Theme A (diatonic, 16 bars + a pickup eighth), as the solo flute plays it at bar 5. */
const THEME_A: readonly Ev[] = [
  ["C5", 18], ["B4", 3], ["C5", 3], ["D5", 3], ["C5", 3], ["B4", 3], ["A4", 3],
  ["C5", 6], ["C5", 3], ["A4", 3], ["C5", 18], ["B4", 3], ["C5", 3],
  ["A4", 3], ["G4", 3], ["E4", 3], ["F4", 3], ["G4", 27],
  ["F4", 3], ["E4", 3], ["D4", 3], ["E4", 3], ["F4", 3], ["G4", 3], ["A4", 3], ["G4", 27],
  ["A4", 3], ["B4", 3], ["A4", 3], ["G4", 3], ["F4", 3], ["E4", 3], ["D4", 3],
  ["E4", 3], ["D4", 3], ["C4", 12], ["C4", 3], ["D4", 3], ["E4", 6], ["F4", 6],
  ["D4", 12], ["G4", 54],
  [null, 6],
  ["D5", 21], ["C5", 3], ["B4", 3], ["A4", 3], ["B4", 3], ["C5", 3],
  ["D5", 3], ["C5", 3], ["B4", 9], ["C5", 3], ["B4", 3], ["A4", 3], ["C5", 3], ["B4", 3], ["A4", 3], ["F4", 9],
  ["F4", 3], ["F4", 3], ["F4", 6], ["A4", 6], ["C5", 3], ["A4", 3], ["B4", 3], ["G4", 3],
  ["F4", 6], ["F4", 3], ["F4", 3], ["F4", 6], ["A4", 6], ["B4", 3], ["G4", 3], ["A4", 3], ["F4", 3],
  ["D4", 6], ["D4", 3], ["C4", 3], ["D4", 18], ["D4", 3], ["D4", 3],
  ["D4", 6], ["F4", 6], ["A4", 3], ["F4", 3], ["G4", 3], ["E4", 3], ["D4", 6], ["D4", 3], ["C4", 3],
  ["D4", 18], ["D4", 3], ["C4", 3], ["D4", 6], ["E4", 3], ["F4", 3],
  ["G4", 27], ["F4", 3], ["E4", 3], ["D4", 3],
  ["C4", 6],
];

/**
 * Theme B (the Phrygian-tinged arabesque), 16 bars falling two octaves from
 * B♭4 to C3, as the high bassoon plays it at Fig. 2.
 */
const THEME_B: readonly Ev[] = [
  ["Bb4", 15], ["A4", 3], ["G4", 3], ["F4", 3], ["Bb4", 3], ["C5", 3], ["A4", 3], ["G4", 3],
  ["Bb4", 6], ["A4", 3], ["G4", 3], ["Bb4", 12], ["A4", 3], ["Bb4", 3], ["A4", 3], ["G4", 18],
  ["F4", 3], ["E4", 3], ["D4", 3], ["E4", 24],
  [null, 6], ["Bb4", 6], ["C5", 6], ["Db5", 12],
  ["Db5", 12], ["Db5", 6], ["Db5", 6], ["Db5", 6],
  ["Db5", 4], ["Db5", 4], ["Db5", 4], ["Db5", 6], ["C5", 3], ["Bb4", 3], ["Db5", 6], ["C5", 3], ["Bb4", 3],
  ["Db5", 3], ["C5", 3], ["Bb4", 3], ["Ab4", 3], ["G4", 3], ["F4", 3], ["E4", 48],
  [null, 6],
  ["D4", 18], ["E4", 6], ["D4", 3], ["E4", 3], ["F4", 18],
  ["G4", 6], ["Ab4", 6], ["F4", 4], ["G4", 4], ["E4", 4],
  ["D4", 3], ["E4", 3], ["D4", 3], ["C4", 9], ["Bb3", 21],
  ["C4", 3], ["Bb3", 3], ["C4", 3], ["D4", 3], ["E4", 3], ["D4", 3], ["C4", 3], ["D4", 3], ["C4", 3], ["Bb3", 3], ["Ab3", 3],
  ["Bb3", 3], ["Ab3", 3], ["G3", 12], ["F3", 33],
  ["G3", 3], ["F3", 3], ["Eb3", 27],
  ["F3", 3], ["Eb3", 3], ["F3", 3], ["Db3", 30],
  ["Db3", 3], ["Eb3", 3], ["Db3", 3], ["F3", 3], ["Eb3", 3], ["Db3", 3], ["C3", 15],
];

/**
 * Where the final statement leaves theme B: instead of sinking to C it holds G,
 * climbs back, and turns the tune into E major for eight bars (bar 327) before
 * dragging it home. Written in theme B's octave; the statement plays it 8va.
 */
const THEME_B_SHARED_EVENTS = 73;

const FINAL_TAIL: readonly Ev[] = [
  ["G3", 54],
  [null, 6], ["D4", 24],
  ["E4", 18],
  ["D4", 3], ["E4", 3], ["F4", 18], ["G4", 6], ["B4", 21],
  ["A4", 3], ["G#4", 3], ["F#4", 3], ["G#4", 3], ["A4", 3], ["B4", 3], ["C5", 3],
  ["B4", 15], ["C5", 3], ["D5", 3], ["C5", 3], ["B4", 3], ["A4", 3], ["G#4", 3], ["F#4", 3],
  ["G#4", 3], ["A4", 3], ["B4", 9], ["C5", 3], ["D5", 3], ["C5", 3], ["B4", 3], ["A4", 3], ["G#4", 3], ["F#4", 3],
  ["G#4", 3], ["F#4", 3], ["E4", 18], ["D4", 3], ["C4", 3], ["D4", 3], ["E4", 27],
  ["F4", 3], ["G4", 3], ["F4", 3], ["E4", 27],
  ["D4", 3], ["C4", 3], ["D4", 3], ["E4", 27],
  ["F#4", 3], ["G#4", 3], ["F#4", 3], ["E4", 27],
  ["D4", 3], ["E4", 3], ["D4", 3], ["C4", 3],
];

const THEME_FINAL: readonly Ev[] = [
  ...THEME_B.slice(0, THEME_B_SHARED_EVENTS),
  ...FINAL_TAIL,
];

/** The Phrygian plunge of bar 339, one octave below the piccolos. */
const CODA_FLOURISH: readonly Ev[] = [
  [null, 6], ["Ab5", 2], ["G5", 2], ["F5", 16], ["Eb5", 2], ["Db5", 2], ["C5", 2], ["Bb4", 2], ["Ab4", 2],
];

const THEMES = { A: THEME_A, B: THEME_B, FINAL: THEME_FINAL } as const;

/** Semitone offset, or a diatonic third above (Ravel's Fig. 16 harmonisation). */
type Recolour = number | "third";

type Statement = {
  theme: keyof typeof THEMES;
  /** First layer leads; the rest double it. */
  melody: readonly { track: number; shift: Recolour }[];
  /** Idle melodic tracks that join the pitched ostinato for this statement. */
  joins: readonly { track: number; note: string }[];
  label: string;
  color: number;
};

const THEME_A_COLOR = 4;
const THEME_B_COLOR = 3;
const TUTTI_COLOR = 0;
const CODA_COLOR = 1;

/**
 * The eighteen statements, following Ravel's own succession of soloists: flute,
 * clarinet, bassoon, E♭ clarinet, oboe d'amore, flute + muted trumpet, tenor
 * and soprano saxophones, then horn with piccolos in G and celesta, and on into
 * the tutti. Fifth-above doublings are Ravel's piccolo in G; the closing
 * statement stacks the tune in fourths.
 */
const STATEMENTS: readonly Statement[] = [
  {
    theme: "A",
    melody: [{ track: TRAVERSO, shift: 0 }],
    joins: [],
    label: "1A flute",
    color: THEME_A_COLOR,
  },
  {
    theme: "A",
    melody: [{ track: HAUTBOIS, shift: 0 }],
    joins: [{ track: TRAVERSO, note: RYTHMIQUE_NOTE }],
    label: "2A clarinet",
    color: THEME_A_COLOR,
  },
  {
    theme: "B",
    melody: [{ track: HAUTBOIS, shift: 0 }],
    joins: [{ track: TRAVERSO, note: RYTHMIQUE_NOTE }],
    label: "3B bassoon",
    color: THEME_B_COLOR,
  },
  {
    theme: "B",
    melody: [{ track: TRAVERSO, shift: 12 }],
    joins: [{ track: HAUTBOIS, note: RYTHMIQUE_NOTE }],
    label: "4B Eb clarinet",
    color: THEME_B_COLOR,
  },
  {
    theme: "A",
    melody: [{ track: HAUTBOIS, shift: 0 }],
    joins: [{ track: TRAVERSO, note: RYTHMIQUE_NOTE }],
    label: "5A oboe d'amore",
    color: THEME_A_COLOR,
  },
  {
    theme: "A",
    melody: [{ track: TRAVERSO, shift: 0 }, { track: CLARION, shift: 0 }],
    joins: [{ track: HAUTBOIS, note: RYTHMIQUE_NOTE }],
    label: "6A flute + muted trumpet",
    color: THEME_A_COLOR,
  },
  {
    // Theme B already sits low; the clarion takes it at pitch rather than 8vb,
    // which would drop the tune under the bass.
    theme: "B",
    melody: [{ track: CLARION, shift: 0 }],
    joins: [
      { track: HAUTBOIS, note: RYTHMIQUE_NOTE },
      { track: TRAVERSO, note: "G5" },
    ],
    label: "7B tenor sax",
    color: THEME_B_COLOR,
  },
  {
    theme: "B",
    melody: [{ track: TRAVERSO, shift: 12 }],
    joins: [{ track: CLARION, note: RYTHMIQUE_NOTE }],
    label: "8B soprano sax",
    color: THEME_B_COLOR,
  },
  {
    theme: "A",
    melody: [
      { track: CLARION, shift: 0 },
      { track: TRAVERSO, shift: 7 },
      { track: MUSIC_BOX, shift: 0 },
    ],
    joins: [{ track: HAUTBOIS, note: RYTHMIQUE_NOTE }],
    label: "9A horn + piccolo 5th + celesta",
    color: THEME_A_COLOR,
  },
  {
    theme: "A",
    melody: [{ track: HAUTBOIS, shift: 0 }, { track: TRAVERSO, shift: 12 }],
    joins: [{ track: CLARION, note: RYTHMIQUE_NOTE }],
    label: "10A oboes + cor anglais",
    color: THEME_A_COLOR,
  },
  {
    theme: "B",
    melody: [{ track: CLARION, shift: 0 }],
    joins: [
      { track: HAUTBOIS, note: RYTHMIQUE_NOTE },
      { track: TRAVERSO, note: "G5" },
    ],
    label: "11B trombone",
    color: THEME_B_COLOR,
  },
  {
    theme: "B",
    melody: [{ track: TRAVERSO, shift: 12 }, { track: HAUTBOIS, shift: 0 }],
    joins: [{ track: CLARION, note: RYTHMIQUE_NOTE }],
    label: "12B woodwinds in octaves",
    color: THEME_B_COLOR,
  },
  {
    theme: "A",
    melody: [
      { track: HAUTBOIS, shift: 0 },
      { track: TRAVERSO, shift: 12 },
      { track: MUSIC_BOX, shift: 0 },
    ],
    joins: [{ track: CLARION, note: RYTHMIQUE_NOTE }],
    label: "13A violins + woodwinds",
    color: THEME_A_COLOR,
  },
  {
    theme: "A",
    melody: [
      { track: HAUTBOIS, shift: 0 },
      { track: TRAVERSO, shift: 12 },
      { track: CLARION, shift: 0 },
      { track: MUSIC_BOX, shift: 0 },
    ],
    joins: [],
    label: "14A violins + woodwinds",
    color: THEME_A_COLOR,
  },
  {
    theme: "B",
    melody: [
      { track: TRAVERSO, shift: 12 },
      { track: HAUTBOIS, shift: 0 },
      { track: CLARION, shift: -12 },
    ],
    joins: [],
    label: "15B + trumpets",
    color: THEME_B_COLOR,
  },
  {
    theme: "B",
    melody: [
      { track: TRAVERSO, shift: 12 },
      { track: HAUTBOIS, shift: 0 },
      { track: CLARION, shift: -12 },
      { track: MUSIC_BOX, shift: 0 },
    ],
    joins: [],
    label: "16B + trombones",
    color: THEME_B_COLOR,
  },
  {
    theme: "A",
    melody: [
      { track: CLARION, shift: 0 },
      { track: HAUTBOIS, shift: "third" },
      { track: TRAVERSO, shift: 7 },
      { track: MUSIC_BOX, shift: 0 },
    ],
    joins: [],
    label: "17A tutti in thirds and fifths",
    color: TUTTI_COLOR,
  },
  {
    theme: "FINAL",
    melody: [
      { track: TRAVERSO, shift: 12 },
      { track: HAUTBOIS, shift: 7 },
      { track: CLARION, shift: 0 },
      { track: MUSIC_BOX, shift: 0 },
    ],
    joins: [],
    label: "18B tutti, final theme",
    color: TUTTI_COLOR,
  },
];

/** Statement `k` enters two bars into its section, i.e. at bar `5 + 18k`. */
function statementEntryBar(index: number): number {
  return 4 + 18 * index;
}

/** Section 0 is Ravel's unnumbered 20-bar opening; 1–17 are 18 bars; 18 is 14. */
function sectionOfBar(bar: number): number {
  if (bar < 20) {
    return 0;
  }
  return Math.min(18, 1 + Math.floor((bar - 20) / 18));
}

/**
 * How the pizzicato strings behave per section: a three-beat tonic/dominant
 * pulse for most of the piece, the arpeggiated tonal ostinato of Figs. 9–10,
 * then hammering the snare rhythm in chords from Fig. 16 to the end.
 */
type Accompaniment =
  | { mode: "pulse"; hi: readonly (string | null)[]; lo: readonly (string | null)[] }
  | { mode: "arpeggio" }
  | { mode: "hammer" };

const SECTION_ACCOMPANIMENT: readonly Accompaniment[] = [
  { mode: "pulse", hi: [null, "G4", null], lo: ["G3", "G3", null] },
  { mode: "pulse", hi: ["G3", "G4", null], lo: ["C3", "G3", "C3"] },
  { mode: "pulse", hi: ["G3", "G4", null], lo: ["C3", "G3", "C3"] },
  { mode: "pulse", hi: ["G3", "G4", "G3"], lo: ["C3", "G3", "C3"] },
  { mode: "pulse", hi: ["G3", null, "C4"], lo: ["C3", "C4", "C3"] },
  { mode: "pulse", hi: ["G3", "C5", "C5"], lo: ["C3", "G3", "C3"] },
  { mode: "pulse", hi: ["G3", "D4", "C4"], lo: ["C3", "G3", "C3"] },
  { mode: "pulse", hi: ["G3", "C5", "C5"], lo: ["C3", "G3", "C3"] },
  { mode: "pulse", hi: ["G3", "G4", "G4"], lo: ["C3", "G3", "C3"] },
  { mode: "arpeggio" },
  { mode: "arpeggio" },
  { mode: "pulse", hi: ["G4", "C5", "C5"], lo: ["C3", "C3", "C3"] },
  { mode: "pulse", hi: ["G3", "C5", "C5"], lo: ["C3", "C3", "C3"] },
  { mode: "pulse", hi: ["G4", "E5", "D5"], lo: ["C3", "C4", "B3"] },
  { mode: "pulse", hi: ["G4", "E5", "D5"], lo: ["C3", "C4", "B3"] },
  { mode: "pulse", hi: ["G4", "E5", "D5"], lo: ["C3", "C4", "B3"] },
  { mode: "hammer" },
  { mode: "hammer" },
  { mode: "hammer" },
];

/** Fig. 9's tonal ostinato: the violas arpeggiate C–G–G–C through the snare rhythm. */
const ARPEGGIO_TRIPLET = [
  { note: "C3", track: PIZZ_LO },
  { note: "G3", track: PIZZ_LO },
  { note: "G4", track: PIZZ_HI },
] as const;
const ARPEGGIO_EIGHTH = { note: "C5", track: PIZZ_HI } as const;

const SWERVE_START_BAR = 326;
const RETURN_START_BAR = 334;
const PLUNGE_BAR = 338;
const FINAL_BAR = 339;

/** Beats 1–2 take the tonic chord, beat 3 the dominant-coloured one. */
type Hammer = { tonic: readonly [string, string]; dominant: readonly [string, string] };

function hammerVoicing(bar: number): Hammer {
  if (bar >= SWERVE_START_BAR && bar < RETURN_START_BAR) {
    return { tonic: ["E5", "G#3"], dominant: ["F#5", "B3"] };
  }
  return { tonic: ["E5", "C4"], dominant: ["D5", "B3"] };
}

/** Winds join the hammered chord for the four bars that swing back to C major. */
const RETURN_HAMMER: readonly { track: number; tonic: string; dominant: string }[] = [
  { track: TRAVERSO, tonic: "E5", dominant: "D5" },
  { track: HAUTBOIS, tonic: "C5", dominant: "B4" },
  { track: CLARION, tonic: "G4", dominant: "G4" },
  { track: MUSIC_BOX, tonic: "C5", dominant: "B4" },
];

const MAJOR_SCALE: readonly number[] = [0, 2, 4, 5, 7, 9, 11];

function diatonicThirdUp(midi: number): number {
  const pitchClass = ((midi % 12) + 12) % 12;
  const degree = MAJOR_SCALE.indexOf(pitchClass);
  if (degree < 0) {
    return midi + 4; // chromatic passing note: parallel major third
  }
  const target = degree + 2;
  return midi - pitchClass + MAJOR_SCALE[target % 7]! + (target >= 7 ? 12 : 0);
}

function recolour(midi: number, shift: Recolour): number {
  return shift === "third" ? diatonicThirdUp(midi) : midi + shift;
}

function noteOf(name: string): number {
  const midi = parseNoteName(name);
  if (midi === undefined) {
    throw new Error(`Boléro demo: unparsable note "${name}"`);
  }
  return midi;
}

function tickToStep(tick: number): number {
  return Math.round(tick / TICKS_PER_STEP);
}

/** `cut` is gate length in sixteenths of a step; 16 steps is the engine's ceiling. */
const MAX_GATE_STEPS = 15;

function gateValue(gateSteps: number): number {
  return Math.min(MAX_GATE_STEPS * 16, Math.max(1, Math.round(gateSteps * 16)));
}

function dynamicAt(bar: number): number {
  const first = DYNAMIC_ANCHORS[0]!;
  if (bar <= first[0]) {
    return first[1];
  }
  for (let i = 1; i < DYNAMIC_ANCHORS.length; i++) {
    const [barTo, levelTo] = DYNAMIC_ANCHORS[i]!;
    if (bar <= barTo) {
      const [barFrom, levelFrom] = DYNAMIC_ANCHORS[i - 1]!;
      const t = (bar - barFrom) / (barTo - barFrom);
      return levelFrom + (levelTo - levelFrom) * t;
    }
  }
  return DYNAMIC_ANCHORS[DYNAMIC_ANCHORS.length - 1]![1];
}

function velocityAt(bar: number, track: number, roleGain: number): number {
  const level = dynamicAt(bar) * roleGain * (TRACK_TRIM[track] ?? 1);
  return Math.min(1, Math.max(0.03, level));
}

function putMidi(
  track: PatternTrack,
  step: number,
  midi: number,
  velocity: number,
  gateSteps: number,
): void {
  track.cells[step] = {
    note: midi,
    volume: velocity,
    fx: [{ kind: "cut", value: gateValue(gateSteps) }],
  };
}

/** Places one event list from `startTick`, recolouring and gating as it goes. */
function renderEvents(
  track: PatternTrack,
  trackIndex: number,
  startTick: number,
  events: readonly Ev[],
  shift: Recolour,
  roleGain: number,
): void {
  let tick = startTick;
  for (const [name, ticks] of events) {
    if (name !== null) {
      const midi = recolour(noteOf(name), shift);
      const end = tick + ticks;
      let at = tick;
      while (at < end) {
        // Ties longer than the gate ceiling have to be re-articulated. Break on
        // the next bar line when one is close enough, since that is where Ravel
        // wrote the tie and a fresh attack there reads as a breath, not a fault.
        const ceiling = at + MAX_GATE_STEPS * TICKS_PER_STEP;
        const barLine = (Math.floor(at / TICKS_PER_BAR) + 1) * TICKS_PER_BAR;
        const until = end <= ceiling ? end : Math.min(ceiling, barLine);
        putMidi(
          track,
          tickToStep(at),
          midi,
          velocityAt(Math.floor(at / TICKS_PER_BAR), trackIndex, roleGain),
          (until - at) / TICKS_PER_STEP,
        );
        at = until;
      }
    }
    tick += ticks;
  }
}

/** Hits of the two-bar ostinato in one bar, as `[tick, durationTicks]`. */
function ostinatoHits(bar: number): readonly (readonly [number, number])[] {
  const offsets = OSTINATO[bar % OSTINATO.length]!;
  return offsets.map((tick, i) => [tick, (offsets[i + 1] ?? TICKS_PER_BAR) - tick] as const);
}

function renderTambour(track: PatternTrack): void {
  const midi = noteOf(TAMBOUR_NOTE);
  for (let bar = 0; bar < PLUNGE_BAR; bar++) {
    const base = bar * STEPS_PER_BAR;
    const velocity = velocityAt(bar, TAMBOUR, TAMBOUR_GAIN);
    for (const [tick] of ostinatoHits(bar)) {
      putMidi(track, base + tickToStep(tick), midi, velocity, GATE.tambour);
    }
  }
  // The pattern finally breaks: two strokes, a gap, then a roll into the last bar.
  const plunge = PLUNGE_BAR * STEPS_PER_BAR;
  for (const tick of [0, 6, 26, 28, 30, 32, 34]) {
    putMidi(track, plunge + tickToStep(tick), midi, velocityAt(PLUNGE_BAR, TAMBOUR, TAMBOUR_GAIN), GATE.tambour);
  }
  putMidi(track, FINAL_BAR * STEPS_PER_BAR, midi, velocityAt(FINAL_BAR, TAMBOUR, TAMBOUR_GAIN), GATE.tambour);
}

function renderPitchedOstinato(
  track: PatternTrack,
  trackIndex: number,
  startBar: number,
  barCount: number,
  name: string,
  roleGain: number,
): void {
  const midi = noteOf(name);
  for (let bar = startBar; bar < startBar + barCount && bar < BAR_COUNT; bar++) {
    const base = bar * STEPS_PER_BAR;
    const velocity = velocityAt(bar, trackIndex, roleGain);
    for (const [tick, ticks] of ostinatoHits(bar)) {
      putMidi(track, base + tickToStep(tick), midi, velocity, Math.min(1.2, ticks / TICKS_PER_STEP));
    }
  }
}

function renderPizzBar(hi: PatternTrack, lo: PatternTrack, bar: number): void {
  const plan = SECTION_ACCOMPANIMENT[sectionOfBar(bar)]!;
  const base = bar * STEPS_PER_BAR;
  const hiVelocity = velocityAt(bar, PIZZ_HI, PIZZ_GAIN);
  const loVelocity = velocityAt(bar, PIZZ_LO, PIZZ_GAIN);

  if (plan.mode === "pulse") {
    for (let beat = 0; beat < 3; beat++) {
      const step = base + beat * 6;
      const high = plan.hi[beat];
      const low = plan.lo[beat];
      if (high) {
        putMidi(hi, step, noteOf(high), hiVelocity, GATE.pizz);
      }
      if (low) {
        putMidi(lo, step, noteOf(low), loVelocity, GATE.pizz);
      }
    }
    return;
  }

  if (plan.mode === "arpeggio") {
    let triplet = 0;
    for (const [tick, ticks] of ostinatoHits(bar)) {
      const voice = ticks === 2 ? ARPEGGIO_TRIPLET[triplet++ % 3]! : ARPEGGIO_EIGHTH;
      const target = voice.track === PIZZ_HI ? hi : lo;
      const velocity = voice.track === PIZZ_HI ? hiVelocity : loVelocity;
      putMidi(target, base + tickToStep(tick), noteOf(voice.note), velocity, GATE.arpeggio);
    }
    return;
  }

  const chords = hammerVoicing(bar);
  for (const [tick] of ostinatoHits(bar)) {
    const [high, low] = tick < 24 ? chords.tonic : chords.dominant;
    putMidi(hi, base + tickToStep(tick), noteOf(high), hiVelocity, GATE.hammer);
    putMidi(lo, base + tickToStep(tick), noteOf(low), loVelocity, GATE.hammer);
  }
}

/**
 * Tonic/dominant pizzicato for most of the piece, an arco eighth-note tread
 * from Fig. 15, then the coda's E major and its F-minor collapse.
 */
function renderBassBar(track: PatternTrack, bar: number): void {
  const base = bar * STEPS_PER_BAR;
  const velocity = velocityAt(bar, CONTRABASS, BASS_GAIN);
  const answering = bar % 2 === 1;
  const place = (tick: number, name: string, gate: number): void => {
    putMidi(track, base + tickToStep(tick), noteOf(name), velocity, gate);
  };

  if (bar < 272) {
    place(0, "C3", GATE.bassPizz);
    place(24, "G2", answering ? 1.4 : GATE.bassPizz);
    if (answering) {
      place(30, "G3", 1.4);
    }
    return;
  }
  if (bar === PLUNGE_BAR) {
    place(0, "F3", 1.4);
    place(6, "F3", 8);
    for (const tick of [24, 26, 28, 30, 32, 34]) {
      place(tick, "F3", GATE.hammer);
    }
    return;
  }
  if (bar === FINAL_BAR) {
    place(0, "C3", 6);
    return;
  }

  const swerve = bar >= SWERVE_START_BAR && bar < RETURN_START_BAR;
  const tonic = swerve ? "E3" : "C4";
  const dominant = swerve ? "B2" : "G3";
  place(0, tonic, GATE.bassArco);
  place(12, tonic, GATE.bassArco);
  place(24, dominant, GATE.bassArco);
  if (answering) {
    place(30, dominant, GATE.bassArco);
  }
}

/** Bars 335–338 put the winds on the hammered chord, then bar 339 plunges. */
function renderCoda(voice: (index: number) => PatternTrack): void {
  for (let bar = RETURN_START_BAR; bar < PLUNGE_BAR; bar++) {
    const base = bar * STEPS_PER_BAR;
    for (const part of RETURN_HAMMER) {
      const track = voice(part.track);
      const velocity = velocityAt(bar, part.track, DOUBLE_GAIN);
      for (const [tick] of ostinatoHits(bar)) {
        const name = tick < 24 ? part.tonic : part.dominant;
        putMidi(track, base + tickToStep(tick), noteOf(name), velocity, GATE.hammer);
      }
    }
  }

  const plungeTick = PLUNGE_BAR * TICKS_PER_BAR;
  renderEvents(voice(TRAVERSO), TRAVERSO, plungeTick, CODA_FLOURISH, 0, LEAD_GAIN);
  renderEvents(voice(HAUTBOIS), HAUTBOIS, plungeTick, CODA_FLOURISH, -12, LEAD_GAIN);
  renderEvents(voice(MUSIC_BOX), MUSIC_BOX, plungeTick, CODA_FLOURISH, 0, DOUBLE_GAIN);
  // Brass holds the F-minor chord under the descent instead of doubling it.
  const plungeStep = PLUNGE_BAR * STEPS_PER_BAR;
  for (const [tick] of ostinatoHits(PLUNGE_BAR)) {
    putMidi(
      voice(CLARION),
      plungeStep + tickToStep(tick),
      noteOf("F4"),
      velocityAt(PLUNGE_BAR, CLARION, LEAD_GAIN),
      GATE.hammer,
    );
  }
  for (const tick of [0, 6, 24, 26, 28, 30, 32, 34]) {
    putMidi(voice(PIZZ_HI), plungeStep + tickToStep(tick), noteOf("Ab4"), velocityAt(PLUNGE_BAR, PIZZ_HI, PIZZ_GAIN), GATE.hammer);
    putMidi(voice(PIZZ_LO), plungeStep + tickToStep(tick), noteOf("F3"), velocityAt(PLUNGE_BAR, PIZZ_LO, PIZZ_GAIN), GATE.hammer);
  }

  // One fortissimo C major, then silence for the rest of the bar.
  const finalStep = FINAL_BAR * STEPS_PER_BAR;
  const finalChord: readonly (readonly [number, string, number])[] = [
    [TRAVERSO, "G5", LEAD_GAIN],
    [HAUTBOIS, "E5", LEAD_GAIN],
    [CLARION, "C5", LEAD_GAIN],
    [MUSIC_BOX, "C5", DOUBLE_GAIN],
    [PIZZ_HI, "E4", PIZZ_GAIN],
    [PIZZ_LO, "C4", PIZZ_GAIN],
  ];
  for (const [track, name, gain] of finalChord) {
    putMidi(voice(track), finalStep, noteOf(name), velocityAt(FINAL_BAR, track, gain), 8);
  }
}

function buildRowMarkers(): Record<string, RowMarker> {
  const markers: Record<string, RowMarker> = {
    "0": { label: "Intro · tambour + pizz", color: 7 },
  };
  for (let k = 0; k < STATEMENTS.length; k++) {
    const statement = STATEMENTS[k]!;
    const step = statementEntryBar(k) * STEPS_PER_BAR;
    markers[String(step)] = { label: statement.label, color: statement.color };
  }
  markers[String(SWERVE_START_BAR * STEPS_PER_BAR)] = { label: "Swerve to E major", color: CODA_COLOR };
  markers[String(RETURN_START_BAR * STEPS_PER_BAR)] = { label: "Back to C major", color: CODA_COLOR };
  markers[String(PLUNGE_BAR * STEPS_PER_BAR)] = { label: "Coda · F minor plunge", color: CODA_COLOR };
  return markers;
}

/**
 * Builds the 340-bar clip: 6120 steps at 72 BPM on a sextuplet grid, a shade
 * over fourteen minutes. Loop stays on so the tracker can wrap when desired.
 */
export function createBoleroDemoClip(): ClipIR {
  const clip = createEmptyClip({
    bpm: BPM,
    steps: BOLERO_TOTAL_STEPS,
    stepDivision: STEP_DIVISION,
  });

  const pattern = clip.patterns[0]!;
  const tracks = pattern.tracks;
  for (let i = 0; i < tracks.length; i++) {
    tracks[i]!.name = BOLERO_TRACK_NAMES[i] ?? tracks[i]!.name;
  }
  const voice = (index: number): PatternTrack => tracks[index]!;

  renderTambour(voice(TAMBOUR));
  for (let bar = 0; bar < BAR_COUNT; bar++) {
    if (bar < PLUNGE_BAR) {
      renderPizzBar(voice(PIZZ_HI), voice(PIZZ_LO), bar);
    }
    renderBassBar(voice(CONTRABASS), bar);
  }

  for (let k = 0; k < STATEMENTS.length; k++) {
    const statement = STATEMENTS[k]!;
    const startTick = statementEntryBar(k) * TICKS_PER_BAR;
    const events = THEMES[statement.theme];
    statement.melody.forEach((layer, index) => {
      renderEvents(
        voice(layer.track),
        layer.track,
        startTick,
        events,
        layer.shift,
        index === 0 ? LEAD_GAIN : DOUBLE_GAIN,
      );
    });
    for (const join of statement.joins) {
      renderPitchedOstinato(
        voice(join.track),
        join.track,
        statementEntryBar(k),
        18,
        join.note,
        RYTHMIQUE_GAIN,
      );
    }
  }

  renderCoda(voice);
  pattern.rowMarkers = buildRowMarkers();
  return clip;
}
